import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import {
  VehicleChargeGrade,
  VehicleFuelGrade,
  VehicleFuelType,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import GasStationMpProvider from "@/core/data-provider/data-mp/gas-station/gas-station-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import { IS_DEV } from "@/domains/main/constants/other";
import {
  selectedFuelAmountSelector,
  selectedFuelGradeSelector,
  selectedPaymentTypeSelector,
  setNewVehicleFuelAmount,
  setRefuelingTime,
  setSelectedFuelAmount,
  setSelectedFuelGrade,
  setSelectedFuelType,
  setSelectedPaymentType,
  setSelectedProductId,
  stationSelector,
  vehicleSelector,
} from "@/domains/gasStation/slice";
import toast from "@/domains/toasts/helpers/toast.ts";

export default () => {
  const { t } = useTranslation("gasStation");
  const dispatch = useAppDispatch();
  const station = useAppSelector(stationSelector);
  const vehicle = useAppSelector(vehicleSelector);
  const selectedFuelAmount = useAppSelector(selectedFuelAmountSelector);
  const selectedFuelGrade = useAppSelector(selectedFuelGradeSelector);
  const selectedPaymentType = useAppSelector(selectedPaymentTypeSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const payHandler = useCallback(
    async (productId: number) => {
      if (vehicle && station) {
        if (IS_DEV) {
          setIsSubmitting(true);
          setTimeout(() => {
            setIsSubmitting(false);
          }, 1000);
        } else {
          try {
            setIsSubmitting(true);
            const response = await GasStationMpProvider.pay({
              amount: selectedFuelAmount,
              gasStationId: station.id,
              gasStationProductId: productId,
              paymentType: selectedPaymentType,
              vehicleMpId: vehicle.id,
            });

            if (response instanceof RpcMpProviderSuccessResponse) {
              dispatch(setRefuelingTime(new Date(Date.now() + response.data.seconds * 1000)));
              dispatch(
                setNewVehicleFuelAmount(response.data.vehicle.fuelAmount - vehicle.fuelAmount),
              );
              toast.success(
                t(
                  `gasStation.${vehicle.fuelType === VehicleFuelType.Electric ? "successElectricFill" : "successGasFill"}`,
                  response.data,
                ),
              );
              toast.success(
                t(
                  `gasStation.${vehicle.fuelType === VehicleFuelType.Electric ? "chargingStarted" : "fillingStarted"}`,
                ),
              );
            } else {
              toast.error("gasStation.errors.FILL_ERROR");
              console.log("payHandler error", response);
            }
          } catch (e) {
            console.log(e);
            toast.error("gasStation.errors.FILL_SYSTEM_ERROR");
          } finally {
            setIsSubmitting(false);
          }
        }
      }
    },
    [vehicle, station, selectedFuelAmount, selectedFuelGrade, selectedPaymentType, dispatch, t],
  );

  const changeFuelTypeHandler = useCallback(
    (
      fuelType: VehicleFuelType,
      grade: VehicleFuelGrade | VehicleChargeGrade | null,
      productId: number,
    ) => {
      dispatch(setSelectedFuelType(fuelType));
      dispatch(setSelectedFuelGrade(grade));
      dispatch(setSelectedProductId(productId));
    },
    [dispatch],
  );

  const changeFuelQuantityHandler = useCallback(
    (value: number) => {
      dispatch(setSelectedFuelAmount(value));
    },
    [dispatch],
  );

  const changePaymentTypeHandler = useCallback(
    (value: PaymentType) => {
      dispatch(setSelectedPaymentType(value));
    },
    [dispatch],
  );

  return {
    isSubmitting,
    payHandler,
    changeFuelTypeHandler,
    changeFuelQuantityHandler,
    changePaymentTypeHandler,
  };
};
